import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useCurrentSport from '@sorare/core/src/hooks/useCurrentSport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import useEvents from '@sorare/core/src/lib/events/useEvents';
import { transferMarket } from '@sorare/core/src/lib/glossary';
import {
  ClickAfterPurchase_Action,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import PrimaryBuyPreview from 'components/primaryBuy/PrimaryBuyPreview';

import {
  PrimaryBuyModalGridQuery,
  PrimaryBuyModalGridQueryVariables,
} from './__generated__/index.graphql';

const GridWrapper = styled(Vertical).attrs({ gap: 2 })``;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
  }
`;

const PRIMARY_BUY_MODAL_GRID_QUERY = gql`
  query PrimaryBuyModalGridQuery(
    $playerSlugs: [String!]!
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
  ) {
    anyPlayers(slugs: $playerSlugs) {
      nodes {
        slug
        anyCardWithLivePrimaryOffer(rarity: limited) {
          slug
          ...PrimaryBuyPreview_anyCard
        }
      }
    }
  }
  ${PrimaryBuyPreview.fragments.anyCard}
` as TypedDocumentNode<
  PrimaryBuyModalGridQuery,
  PrimaryBuyModalGridQueryVariables
>;

type PrimaryBuyModalGridQuer_player =
  PrimaryBuyModalGridQuery['anyPlayers']['nodes'][number];

type Props = {
  playerSlugs: string[];
  onPrimaryBuySuccess: () => void;
  gameSlug: string;
  onInfoClick?: (cardSlug: string, playerSlug: string) => void;
  startingLineup?: string[];
};

export const Grid = ({
  playerSlugs,
  onPrimaryBuySuccess,
  gameSlug,
  onInfoClick,
  startingLineup,
}: Props) => {
  const track = useEvents();
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();
  const sport = useCurrentSport();

  const { data, loading } = useQuery(PRIMARY_BUY_MODAL_GRID_QUERY, {
    variables: {
      playerSlugs: playerSlugs || [],
      isIosApp,
      isAndroidApp,
      googlePlayStoreCountryCode,
    },
    skip: !playerSlugs?.length,
  });

  const players = (data?.anyPlayers?.nodes || []).reduce<
    Record<string, PrimaryBuyModalGridQuer_player>
  >((acc, card) => {
    return {
      ...acc,
      [card.slug]: card,
    };
  }, {});

  return (
    <GridWrapper>
      <ItemsGrid>
        {playerSlugs.map(p => {
          if (!players[p]) return null;
          const { anyCardWithLivePrimaryOffer: card, slug: playerSlug } =
            players[p];

          if (!card?.latestPrimaryOffer?.price) return null;

          const { slug, latestPrimaryOffer } = card;

          const trackAfterPurchase = (action: ClickAfterPurchase_Action) => {
            track('Click After Purchase', {
              offerId: latestPrimaryOffer!.id,
              cardSlug: slug!,
              gameSlug,
              sport,
              action,
            });
          };

          return (
            <PrimaryBuyPreview
              key={latestPrimaryOffer!.id}
              card={card!}
              width={320}
              origin={ClickInstantBuy_Flow.PRE_MATCH}
              starting={startingLineup?.includes(playerSlug)}
              primaryBuyConfirmationOptions={{
                variant: 'rivals',
                actions: {
                  primary: {
                    onClick: () => {
                      trackAfterPurchase(ClickAfterPurchase_Action.BUY_MORE);
                    },
                    label: <FormattedMessage {...transferMarket.buyMore} />,
                  },
                  secondary: {
                    onClick: () => {
                      trackAfterPurchase(
                        ClickAfterPurchase_Action.SET_MY_LINEUP
                      );
                      onPrimaryBuySuccess();
                    },
                    label: (
                      <FormattedMessage
                        defaultMessage="Compose your team"
                        id="primaryBuyModalGrid.grid.composeYourTeam"
                      />
                    ),
                  },
                },
              }}
              onInfoClick={onInfoClick}
            />
          );
        })}
      </ItemsGrid>
      {loading && <LoadingIndicator />}
    </GridWrapper>
  );
};
